/* eslint-disable no-nested-ternary */
import styled, { css } from 'styled-components';

import { Link } from '@powdr/components/link';
import {
  Components,
  ComponentGroups,
  Breakpoints,
  ThemeProps as Prop,
  AlignmentShorthand,
  FontFamily,
} from '@powdr/constants';
import {
  themeUtil, componentFontSize, getFontStyles,
} from '@powdr/utils';

const CPNT = Components.CONTENT_BLOCK_HERO;

const responsiveShowHide = css`
  &.mobile {
    ${Breakpoints.desktopOnly(css`
      display: none;
    `)}
  }

  &.desktop {
    ${Breakpoints.mobile(css`
      display: none;
    `)}
  }
`;

const rowColumnMap = {
  [AlignmentShorthand.TOP_LEFT]: { col: 'left', row: 'top' },
  [AlignmentShorthand.CENTER_LEFT]: { col: 'left', row: 'center' },
  [AlignmentShorthand.BOTTOM_LEFT]: { col: 'left', row: 'bottom' },
  [AlignmentShorthand.TOP_CENTER]: { col: 'center', row: 'top' },
  [AlignmentShorthand.CENTER_CENTER]: { col: 'center', row: 'center' },
  [AlignmentShorthand.BOTTOM_CENTER]: { col: 'center', row: 'bottom' },
  [AlignmentShorthand.TOP_RIGHT]: { col: 'right', row: 'top' },
  [AlignmentShorthand.CENTER_RIGHT]: { col: 'right', row: 'center' },
  [AlignmentShorthand.BOTTOM_RIGHT]: { col: 'right', row: 'bottom' },
};

const contentAlignmentStyles = (contentAlignment) => {
  const { col } = rowColumnMap[contentAlignment] || { col: 'center' };

  switch (col) {
    case 'left':
      return 'align-items: start;';
    case 'right':
      return 'align-items: end;';
    case 'center':
      return 'align-items: center;';

    default:
      return 'align-items: left;';
  }
};

export const SlideContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({
    $isAltCopyDisplay,
    $contentAlignment,
  }) => (($isAltCopyDisplay) ? '' : `
    padding-bottom: ${(
      ($contentAlignment === AlignmentShorthand.CENTER_CENTER
      || $contentAlignment === AlignmentShorthand.CENTER_LEFT
      || $contentAlignment === AlignmentShorthand.CENTER_RIGHT))
      && 0}px;
  `)}

  ${Breakpoints.desktopOnly(css`
    ${({ $contentAlignment }) => contentAlignmentStyles($contentAlignment)};
  `)}

  ${Breakpoints.mobile(css`
    padding-left: 35px;
    padding-right: 35px;
    align-items: left;
  `)}

  ${responsiveShowHide}
`;

export const HeroIconWrapper = styled.div`
  display: block;
  width: fit-content;
  margin-bottom: 10px;

  svg {
    fill: #FFFFFF;
    color: #FFFFFF;
  }

  ${Breakpoints.mobile(css`
    margin: 0 auto 10px auto;
  `)}
`;

const contentBackgroundColor = (colorProfile) => css`${(colorProfile) ? `
    text-shadow: none;
    background-color: ${themeUtil(Prop.BACKGROUND, CPNT, colorProfile)};
    color: ${themeUtil(Prop.HEADER, CPNT, colorProfile)};`
  : ''
}`;

const copyStyles = css`
  width: 100%;
  ${({ $isDisableTextShadow }) => (($isDisableTextShadow) ? '' : 'text-shadow: 2px 2px 10px #000;')};
  color: #fff;
  //max-width: 950px;

  ${Breakpoints.smaller(css`
    //max-width: 500px;
    a h2 span {
      width: 90%;
    }
  `)}

  ${Breakpoints.large(css`
    //max-width: 1050px;
  `)}

  ${Breakpoints.larger(css`
    //max-width: 1150px;
  `)}
`;

export const InlineImageWrapper = styled.div`
  width: 100%;
`;

export const ContentImage = styled.img`
  width: ${({ $imageWidth }) => `${$imageWidth}%`};
  margin-bottom: 10px;

  ${Breakpoints.mobile(css`
    width: ${({ $imageWidthMobile }) => `${$imageWidthMobile}%`};
  `)}
`;

export const CopyHeader = styled.h1`
  ${copyStyles}
  ${({ $colorProfile }) => (contentBackgroundColor($colorProfile))};
  padding-bottom: ${({ $isDisablePadding }) => (($isDisablePadding) ? '0' : '10px')};

  ${() => getFontStyles(FontFamily.PRIMARY_FONT, undefined, 2, undefined, CPNT, 'header')};

  ${Breakpoints.small(css`
    ${() => getFontStyles(FontFamily.PRIMARY_FONT, undefined, 1.75, undefined, CPNT, 'header')};
  `)}

  ${Breakpoints.smaller(css`
    ${() => getFontStyles(FontFamily.PRIMARY_FONT, undefined, 1.5, undefined, CPNT, 'header')};
  `)}

  ${Breakpoints.mobile(css`
    ${() => getFontStyles(FontFamily.PRIMARY_FONT, undefined, undefined, undefined, CPNT, 'headerMobile')};
  `)}
`;

export const CopyHeaderEmbedded = styled.h1`
  ${copyStyles}
  ${({ $colorProfile }) => (contentBackgroundColor($colorProfile))};
  ${({ theme }) => theme.fonts.PRI_HDR_BLK};
  ${() => componentFontSize(ComponentGroups.HERO, 'title-smaller-bp')};
  padding-bottom: ${({ $isDisablePadding }) => (($isDisablePadding) ? '0' : '10px')};
  margin-bottom: 50px;
`;

export const CopyContent = styled.p`
  ${copyStyles}
  ${({ $colorProfile }) => (contentBackgroundColor($colorProfile))};
  line-height: 1.2;

  ${() => getFontStyles(FontFamily.BASE_FONT, undefined, 1.5, undefined, CPNT, 'copy')};

  ${Breakpoints.large(css`
    ${() => getFontStyles(FontFamily.BASE_FONT, undefined, 1.75, undefined, CPNT, 'copy')};
  `)}

  ${Breakpoints.small(css`
    ${() => getFontStyles(FontFamily.BASE_FONT, undefined, 1.5, undefined, CPNT, 'copy')};
  `)}

  ${Breakpoints.mobile(css`
    ${() => getFontStyles(FontFamily.BASE_FONT, undefined, 1.5, undefined, CPNT, 'copy')};
  `)}
`;

export const SlideButton = styled(Link)`
  display: inline-block;
  padding: 16px 20px;
  text-decoration: none;
  width: fit-content;
  text-align: center;
  color: ${() => themeUtil(Prop.BTN_TXT, CPNT)};
  background-color: ${() => themeUtil(Prop.BTN_BG, CPNT)};
  transition: color 0.25s ease-out, background-color 0.25s ease-out;
  margin-top: 30px;
  ${() => getFontStyles(FontFamily.SECONDARY_FONT, undefined, undefined, undefined, CPNT, 'button')};

  &:hover, &:focus {
    transition: color 0.25s ease-out, background-color 0.25s ease-out;
    color: ${() => themeUtil(Prop.BTN_TXT_HOVER, CPNT)};
    background-color: ${() => themeUtil(Prop.BTN_BG_HOVER, CPNT)};
  }

  &:focus {
    outline: 2px solid ${() => themeUtil(Prop.BTN_BG_HOVER, CPNT)};
    outline-offset: 2px;
  }

  ${Breakpoints.mobile(css`
    ${() => getFontStyles(FontFamily.SECONDARY_FONT, undefined, 0.8, undefined, CPNT, 'button')};
  `)}
`;

export const EmbeddedVideoButton = styled.button`
  width: 50px;
  height: 50px;
`;
